import useDeepcellError, { ReasonCode } from 'components/shared/useDeepcellError'
import { useEffect, useState } from 'react'
import { notificationComponent } from 'redux/slices/NotificationSlice'
import { usePlotlySlice } from 'redux/slices/hooks'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import CellVisualizationDirtyPrompt from './CellVisualizationDirtyPrompt'
import { CellVisualizationScene } from './CellVisualizationScene'
import useLoadCellInfoEffect from './arrow/useLoadCellInfoEffect'
import { PlotDataContextProvider } from './plot/usePlotData'
import { EXErrorWithMessage } from './useSessionApi'
import MorphotypeMigrationDialog from './MorphotypeMigrationDialog'

export const CellVisualizationsPage = (): JSX.Element => {
  const { showError: showDeepcellError } = useDeepcellError()
  const { clearStore } = useCellVisualizationsSlice()
  const { resetRange } = usePlotlySlice()

  const [migrationDialogState, setMigrationDialogState] = useState<{
    open: boolean
    migrationStep: 'idle' | 'morphotypes' | 'differential-features' | 'complete'
    migratedMorphotypesCount: number
  }>({
    open: false,
    migrationStep: 'idle',
    migratedMorphotypesCount: 0,
  })

  const [userDismissedMigrationDialog, setUserDismissedMigrationDialog] = useState(false)

  // Wrapper that prevents reopening if user dismissed
  const setMigrationDialogStateWrapper = (state: {
    open: boolean
    migrationStep: 'idle' | 'morphotypes' | 'differential-features' | 'complete'
    migratedMorphotypesCount: number
  }) => {
    // If user has dismissed the dialog, don't reopen it
    if (userDismissedMigrationDialog && state.open) {
      return
    }
    setMigrationDialogState(state)
  }

  // TODO: Need to handle the errors
  const handleError = (error: unknown) => {
    const deepcellError = error as EXErrorWithMessage
    showDeepcellError(deepcellError?.data?.reason as ReasonCode, {
      message: deepcellError?.data?.error,
      component: notificationComponent.Toaster,
      severity: 'error',
    })
  }
  useLoadCellInfoEffect(handleError, setMigrationDialogStateWrapper)

  // Use useEffect to return a cleanup function that runs when we leave this page
  // The dependencies below (clearStore and resetRange) should remain stable between re-renders
  // So React should run the cleanup function only once when the CellVisualizationPage component unmounts
  //
  // This code handles clearing all of the global state that lives outside of the CellVisualizationPage's
  // components when you leave the page, instead of our past appraoch of clearing the state
  // in multiple places as you enter the page. (fix for SWT-749)
  useEffect(() => {
    return () => {
      clearStore()
      resetRange()
    }
  }, [clearStore, resetRange])

  const handleCloseMigrationDialog = () => {
    setMigrationDialogState({
      open: false,
      migrationStep: 'idle',
      migratedMorphotypesCount: 0,
    })
    // Mark that user has dismissed the dialog to prevent reopening
    setUserDismissedMigrationDialog(true)
  }

  return (
    <PlotDataContextProvider>
      <CellVisualizationScene />
      <CellVisualizationDirtyPrompt />
      <MorphotypeMigrationDialog
        open={migrationDialogState.open}
        migrationStep={migrationDialogState.migrationStep}
        migratedMorphotypesCount={migrationDialogState.migratedMorphotypesCount}
        onClose={handleCloseMigrationDialog}
      />
    </PlotDataContextProvider>
  )
}

export default CellVisualizationsPage
